import type { AxiosError } from 'axios';

import { INTERVENTIONS_API_URL } from 'constants/InterventionConstants';
import { TECHNICIAN } from 'constants/ModeConstants';
import * as actionTypes from 'modules/interventions/actionTypes';

import type * as types from '../types/actions';
import type { KanbanEnum } from '../types/interfaces/kanban';
import type { DetailedKanbanIntervention } from '../types/projections/detailed-kanban-intervention';
import type { KanbanIntervention } from '../types/projections/kanban-intervention';
import type { InterventionsKanbanQsRequest } from '../types/requests/interventions-kanban-qs-request';

// KANBAN FETCH OPERATIONS

export const fetchKanbanInterventions = (
  params: Partial<InterventionsKanbanQsRequest>,
  meta: Record<string, unknown> = {},
): types.FetchKanbanInterventionsAction => ({
  type: actionTypes.FETCH_KANBAN_INTERVENTIONS,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[TECHNICIAN]}/kanban`,
      params: {
        ...params,
        mode: TECHNICIAN,
      },
    },
  },
  meta,
});

export const fetchKanbanInterventionsSuccess = (
  response: Record<KanbanEnum, KanbanIntervention[]>,
  meta: Record<string, unknown> = {},
): types.FetchKanbanInterventionsSuccessAction => ({
  type: actionTypes.FETCH_KANBAN_INTERVENTIONS_SUCCESS,
  payload: response,
  meta,
});

export const fetchKanbanInterventionsFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.FetchKanbanInterventionsFailureAction => ({
  type: actionTypes.FETCH_KANBAN_INTERVENTIONS_FAILURE,
  payload: error,
  meta,
  error: true,
});

// KANBAN DETAILS

export const fetchInterventionKanbanDetails = (
  interventionId: string,
  mode: keyof typeof INTERVENTIONS_API_URL,
  meta: Record<string, unknown> = {},
): types.FetchInterventionKanbanDetailsRequestAction => ({
  type: actionTypes.FETCH_INTERVENTION_KANBAN_DETAILS_REQUEST,
  payload: {
    interventionId,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode]}/${interventionId}`,
      params: {
        projection: 'detailsKanban',
      },
    },
  },
  meta,
});

export const fetchInterventionKanbanDetailsSuccess = (
  intervention: DetailedKanbanIntervention,
  meta: Record<string, unknown> = {},
): types.FetchInterventionKanbanDetailsSuccessAction => ({
  type: actionTypes.FETCH_INTERVENTION_KANBAN_DETAILS_SUCCESS,
  payload: intervention,
  meta,
});

export const fetchInterventionKanbanDetailsFailure = (
  interventionId: string,
  error: unknown,
  meta: Record<string, unknown> = {},
): types.FetchInterventionKanbanDetailsFailureAction => ({
  type: actionTypes.FETCH_INTERVENTION_KANBAN_DETAILS_FAILURE,
  payload: { interventionId, error },
  error: true,
  meta,
});

// KANBAN MOVES

export const moveKanbanIntervention = (
  interventionId: string,
  kanbanStatus: KanbanEnum,
  destinationKanbanStatus: KanbanEnum,
  meta?: Record<string, unknown>,
): types.MoveKanbanInterventionAction => ({
  type: actionTypes.MOVE_KANBAN_INTERVENTION,
  payload: {
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[TECHNICIAN]}/${interventionId}/kanban/status`,
      data: {
        status: destinationKanbanStatus,
      },
    },
    from: kanbanStatus,
    to: destinationKanbanStatus,
    interventionId,
  },
  meta,
});

export const moveKanbanInterventionSuccess = (
  intervention: KanbanIntervention,
  groupId: KanbanEnum,
  meta: Record<string, unknown> = {},
): types.MoveKanbanInterventionSuccessAction => ({
  type: actionTypes.MOVE_KANBAN_INTERVENTION_SUCCESS,
  payload: {
    intervention,
    groupId,
  },
  meta,
});

export const moveKanbanInterventionFailure = (
  error: string | AxiosError,
  rollback: types.RollbackPayload,
  meta: Record<string, unknown> = {},
): types.MoveKanbanInterventionFailureAction => ({
  type: actionTypes.MOVE_KANBAN_INTERVENTION_FAILURE,
  payload: { error, rollback },
  meta,
  error: true,
});
